import { escapeHtml, safeUrl } from '../ui.js';
import { allProjects } from '../db.js';
import { hasToken, resumeToken, ensureToken, listFolder, isFolderMime } from '../google-api.js';
import { folderCode, walkFolder } from '../drive-folders.js';

// FILES · Module A — Site photos (artboard 5b), built to the verbatim design.
// Photos are the one place cards beat rows: folder 07 read as a grid, GROUPED
// BY VISIT DATE rather than by name, because that is how a site visit is
// remembered. This module reproduces ONLY 5b's card panel (lines 166–212) into
// #view; the entry point is wired separately.
//
// Everything data-derived is escaped through escapeHtml(); every data-built
// href goes through safeUrl() (house rendering-safety rule).
//
// OPEN DECISION #5: bulk photo numbering on upload — unresolved; this view only
// READS folder 07 (spec §A3 / open decision #5). It never assigns numbers.

// ---- small pure helpers ---------------------------------------------------
const MONO = 'font-family:var(--font-technical);';
const LOADING = '<div style="padding:48px 0;color:var(--ink-3);font-size:13px">Loading…</div>';

// Card + pill styles — verbatim from 5b (lines 166–172).
const CARD = 'box-sizing:border-box;width:900px;max-width:100%;background:var(--surface);border:1px solid var(--hair);border-radius:6px;padding:26px 30px 30px;display:flex;flex-direction:column;gap:24px;';
const PILL_ACTIVE = 'background:var(--ink-fill);color:var(--ink-fill-ink);border-radius:999px;padding:6px 13px;font-size:12px;cursor:pointer;';
const PILL_INACTIVE = 'border:1px solid var(--hair);border-radius:999px;padding:6px 13px;font-size:12px;cursor:pointer;';
// Tile — grey placeholder square (lines 183–189). tiles are placeholders like
// the artboard; live Drive thumbnails are a follow-up (thumbnailLink needs an
// authorised fetch).
const TILE = 'aspect-ratio:1;background:var(--brief-bg);border:1px solid var(--hair);border-radius:10px;text-decoration:none;color:inherit;cursor:pointer;';
const GRID = 'display:grid;grid-template-columns:repeat(7,1fr);gap:10px;';

function stemOf(name) {
  const s = String(name || '');
  const i = s.lastIndexOf('.');
  return i > 0 ? s.slice(0, i) : s;
}

// Photo numbering grammar: PROJECT-07-ITEM, e.g. 7014-07-0101. The item is the
// 4-digit block after the folder code; parse it, or fall back below to file
// order. Returns { full: "7014-07-0101", block: "0101" } or null.
function parsePhotoNumber(name, projectNumber) {
  const stem = stemOf(name);
  const m = stem.match(/^(\d{4}|A\d{4})-(\d{2})-(\d{2})(\d{2})/);
  if (m) return { full: `${m[1]}-${m[2]}-${m[3]}${m[4]}`, block: m[3] + m[4] };
  // No leading project/07 prefix — still honour a bare -NNNN item block.
  const m2 = stem.match(/-(\d{2})(\d{2})(?!\d)/);
  if (m2) {
    const block = m2[1] + m2[2];
    return { full: `${String(projectNumber || '')}-07-${block}`, block };
  }
  return null;
}

// A visit = a calendar day. Bucket by the image's modifiedTime local day.
function dayKey(iso) {
  const d = new Date(iso);
  if (isNaN(d)) return 'unknown';
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}
function dayLabel(iso) {
  const d = new Date(iso);
  if (isNaN(d)) return 'Undated';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });
}

// Group images into visits (newest visit first); within a visit, order by the
// time each photo was taken (modifiedTime ascending).
function groupByVisit(images, projectNumber) {
  const buckets = new Map();
  images.forEach((f) => {
    const key = dayKey(f.modifiedTime);
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(f);
  });
  const groups = [];
  for (const [, imgs] of buckets) {
    imgs.sort((a, b) => new Date(a.modifiedTime || 0) - new Date(b.modifiedTime || 0));
    // Item-number range from parsed filenames — first→last by taken order.
    const parsed = imgs.map((f) => parsePhotoNumber(f.name, projectNumber));
    const firstP = parsed.find(Boolean);
    const lastP = [...parsed].reverse().find(Boolean);
    let range = '';
    if (firstP && lastP) {
      range = firstP.block === lastP.block ? firstP.full : `${firstP.full} – ${lastP.block}`;
    }
    groups.push({ iso: imgs[0].modifiedTime, label: dayLabel(imgs[0].modifiedTime), imgs, range });
  }
  // Newest visit first.
  groups.sort((a, b) => new Date(b.iso || 0) - new Date(a.iso || 0));
  return groups;
}

// ---- render fragments -----------------------------------------------------
function tileHtml(file, plusN) {
  const href = escapeHtml(safeUrl(file.webViewLink));
  const centred = plusN ? 'display:flex;align-items:center;justify-content:center;font-size:13px;color:var(--ink-2);' : '';
  const body = plusN ? `+${escapeHtml(String(plusN))}` : '';
  return `<a class="ph-tile" href="${href}" target="_blank" rel="noopener" data-photo="${escapeHtml(file.id || '')}" style="${TILE}${centred}">${body}</a>`;
}

function visitHtml(g) {
  const count = g.imgs.length;
  // OPEN: 'who went / why' per visit isn't in Drive metadata — needs a
  // visit-notes source; omitted rather than faked. The artboard's
  // "Sanjeet Panesar · reinforcement check" slot renders as a muted em dash.
  const header = `<div style="display:flex;align-items:baseline;gap:14px;">
      <span style="font-size:10px;font-weight:500;letter-spacing:0.14em;text-transform:uppercase;color:var(--link);">${escapeHtml(g.label)} &#183; ${count} photo${count === 1 ? '' : 's'}</span>
      <span style="font-size:13px;color:var(--ink-3);">&#8212;</span>
      <div style="flex:1;"></div>
      ${g.range ? `<span style="${MONO}font-size:11.5px;color:var(--ink-3);">${escapeHtml(g.range)}</span>` : ''}
    </div>`;
  // Up to 7 tiles; when a visit has more than 7, the 7th carries "+N" (N = the
  // rest), exactly as 5b draws it (14 photos → +7, 22 → +15).
  const shown = g.imgs.slice(0, 7);
  const tiles = shown.map((f, i) => tileHtml(f, i === 6 && count > 7 ? count - 7 : 0)).join('');
  return `<div style="display:flex;flex-direction:column;gap:14px;">${header}<div style="${GRID}">${tiles}</div></div>`;
}

// "All" view — one flat grid of every photo, newest first.
function allHtml(images) {
  const flat = [...images].sort((a, b) => new Date(b.modifiedTime || 0) - new Date(a.modifiedTime || 0));
  return `<div style="${GRID}">${flat.map((f) => tileHtml(f, 0)).join('')}</div>`;
}

// ---- calm-card states (connect gate / empty / error) ----------------------
function calmCard(eyebrow, title, body, eyebrowColor = 'var(--ink-3)') {
  return `
    <div style="background:var(--surface);border:1px solid var(--hair);border-radius:16px;padding:24px 26px 26px;display:flex;flex-direction:column;gap:14px;max-width:520px;">
      <span style="font-size:10px;font-weight:500;letter-spacing:0.14em;text-transform:uppercase;color:${eyebrowColor};">${escapeHtml(eyebrow)}</span>
      <span style="font-size:19px;font-weight:500;letter-spacing:-0.02em;line-height:1.25;">${escapeHtml(title)}</span>
      <span style="font-size:14px;line-height:1.6;color:var(--ink-2);text-wrap:pretty;">${escapeHtml(body)}</span>
    </div>`;
}

function connectGate() {
  return `
    <div style="background:var(--surface);border:1px solid var(--hair);border-radius:16px;padding:24px 26px 26px;display:flex;flex-direction:column;gap:14px;max-width:520px;">
      <span style="font-size:10px;font-weight:500;letter-spacing:0.14em;text-transform:uppercase;color:var(--ink-3);">Site photos · Google Drive</span>
      <span style="font-size:19px;font-weight:500;letter-spacing:-0.02em;line-height:1.25;">Connect Google Drive to see the photos</span>
      <span style="font-size:14px;line-height:1.6;color:var(--ink-2);text-wrap:pretty;">Track reads folder 07 in this project's Drive folder as you — read-only. Nothing is written. The visits and item numbers all come from there.</span>
      <div style="flex:1;"></div>
      <div><span id="ph-connect" style="background:var(--ink-fill);color:var(--ink-fill-ink);border-radius:8px;padding:10px 16px;font-size:13px;font-weight:500;cursor:pointer;">Connect Google Drive</span></div>
    </div>`;
}

// ---- entry point ----------------------------------------------------------
export async function renderPhotos(mount, session, { project, projectNumber, onBack } = {}) {
  // Immediate loading state before any await (house rule).
  mount.innerHTML = LOADING;
  // Only the latest render on this mount paints (Back or another tab, pressed
  // while Drive is still answering, must not be painted over). files.js shares the token.
  const token = {};
  mount.__fxRender = token;
  const stale = () => mount.__fxRender !== token;

  // Resolve the project by NUMBER when one wasn't handed in.
  if (!project) {
    const projects = await allProjects().catch(() => []);
    if (stale()) return;
    project = projects.find((p) => String(p.number) === String(projectNumber));
  }

  const backLink = typeof onBack === 'function'
    ? `<span id="ph-back" style="font-size:12.5px;color:var(--link);cursor:pointer;">&#8592; Back</span>`
    : '';
  const num = project ? String(project.number || projectNumber || '') : String(projectNumber || '');
  const shell = (bodyHtml) => `
    <div style="display:flex;flex-direction:column;gap:14px;">
      <div style="display:flex;align-items:center;gap:12px;">
        ${backLink}
        <span style="font-size:22px;font-weight:500;letter-spacing:-0.02em;">Site photos &#8212; folder 07${num ? ` &#183; ${escapeHtml(num)}` : ''}</span>
      </div>
      <div style="font-size:13px;line-height:1.5;color:var(--ink-2);max-width:860px;">Grouped by visit date rather than by name, because that is how a site visit is remembered.</div>
      <div id="ph-body">${bodyHtml}</div>
    </div>`;
  const wireBack = () => {
    const b = mount.querySelector('#ph-back');
    if (b && typeof onBack === 'function') b.addEventListener('click', () => onBack());
  };

  if (!project) {
    mount.innerHTML = shell(calmCard('Site photos', 'No project found',
      `No project matches number ${projectNumber || '—'}.`));
    wireBack();
    return;
  }

  // Not connected → connect gate (calm card, same pattern as other Files views).
  // An expired token is renewed quietly first, as the register does.
  if (!hasToken()) await resumeToken();
  if (stale()) return;
  if (!hasToken()) {
    mount.innerHTML = shell(connectGate());
    wireBack();
    const btn = mount.querySelector('#ph-connect');
    if (btn) btn.addEventListener('click', () => {
      ensureToken()
        .then(() => renderPhotos(mount, session, { project, projectNumber, onBack }))
        .catch(() => { btn.textContent = 'Google connection cancelled — try again'; });
    });
    return;
  }

  if (!project.folderId) {
    mount.innerHTML = shell(calmCard('Site photos', 'No Drive folder linked',
      `This project has no Drive folder linked, so there is no folder 07 to read. Add a Folder ID to ${project.number} in Projects.`));
    wireBack();
    return;
  }

  // Find the project's folder 07 (Photos): the child whose name starts with "07".
  let photoFolder;
  try {
    const top = await listFolder(project.folderId);
    if (stale()) return;
    // ASP's is "7. Images", one digit: matching "07" found nothing, ever.
    photoFolder = top.find((f) => isFolderMime(f.mimeType) && folderCode(f.name) === '07');
  } catch (e) {
    mount.innerHTML = shell(calmCard('Site photos', 'Drive could not be read',
      `${(e && e.message) || 'Unknown error'}. Reconnect and try again.`, 'var(--brand-orange)'));
    wireBack();
    return;
  }

  if (!photoFolder) {
    mount.innerHTML = shell(calmCard('Site photos', 'No photos folder yet',
      'No site photos folder (07) on this project yet.'));
    wireBack();
    return;
  }

  // List the image files inside folder 07.
  let images;
  try {
    // Photos are filed in per-visit subfolders as often as loose, so walk it.
    const { files } = await walkFolder(listFolder, photoFolder.id, { maxDepth: 2, maxFolders: 40 });
    if (stale()) return;
    images = files.filter((f) => String(f.mimeType || '').startsWith('image/'));
  } catch (e) {
    mount.innerHTML = shell(calmCard('Site photos', 'Drive could not be read',
      `${(e && e.message) || 'Unknown error'}. Reconnect and try again.`, 'var(--brand-orange)'));
    wireBack();
    return;
  }

  if (!images.length) {
    mount.innerHTML = shell(calmCard('Site photos', 'No photos in folder 07 yet',
      'Folder 07 exists but holds no images yet. Photos are numbered in bulk on upload, in the order they were taken.'));
    wireBack();
    return;
  }

  const groups = groupByVisit(images, project.number);
  const total = images.length;
  const visits = groups.length;

  const groupedBody = groups.map(visitHtml).join('');

  const body = `
    <div style="${CARD}">
      <div style="display:flex;align-items:baseline;justify-content:space-between;">
        <span style="font-size:15px;color:var(--ink-2);">${total} photo${total === 1 ? '' : 's'} across ${visits} visit${visits === 1 ? '' : 's'}</span>
        <span style="display:flex;gap:8px;">
          <span class="ph-toggle" data-view="visit" style="${PILL_ACTIVE}">By visit</span>
          <span class="ph-toggle" data-view="all" style="${PILL_INACTIVE}">All ${total}</span>
        </span>
      </div>
      <div id="ph-panel" style="display:flex;flex-direction:column;gap:24px;">${groupedBody}</div>
      <div style="font-size:12.5px;color:var(--ink-3);">Photos are the one file type Track numbers in bulk &#8212; one item number per photo, assigned on upload, in the order they were taken.</div>
    </div>`;

  mount.innerHTML = shell(body);
  wireBack();
  // A photo opens in Track's viewer; ← / → flick through the rest (Karan 08/10).
  // Cmd/Ctrl-click still opens it in Drive.
  mount.addEventListener('click', (e) => {
    const a = e.target.closest('a[data-photo]');
    if (!a || e.metaKey || e.ctrlKey || e.shiftKey) return;
    const f = images.find((x) => x.id === a.dataset.photo);
    if (!f) return;
    e.preventDefault();
    import('./google-panels.js').then(({ openFile }) => openFile(f, { siblings: images }));
  });

  // Wire the By visit / All toggle: swap #ph-panel and repaint the pills.
  const panel = mount.querySelector('#ph-panel');
  const pills = Array.from(mount.querySelectorAll('.ph-toggle'));
  pills.forEach((pill) => pill.addEventListener('click', () => {
    const view = pill.dataset.view;
    pills.forEach((p) => p.setAttribute('style', p.dataset.view === view ? PILL_ACTIVE : PILL_INACTIVE));
    // "By visit" is the default grouped view; "All" is one flat grid.
    panel.innerHTML = view === 'all' ? allHtml(images) : groupedBody;
  }));
}
